import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { 
  HeatMeter, 
  HeatMeterUpdate, 
  BracketRoundType 
} from '@bracketblitz/shared-types'
import { 
  updateHeatMeter, 
  createHeatMeter 
} from '@bracketblitz/game-engine'

export interface ScoreBreakdown {
  base: number
  heat: number
  upset: number
  powerUp: number
  total: number
}

export interface RoundScore {
  round: BracketRoundType
  picks: number
  correct: number
  points: number
  accuracy: number
}

export interface GamingState {
  // Heat Meter
  heatMeter: HeatMeter | null
  
  // Scoring
  totalScore: number
  scoreBreakdown: ScoreBreakdown
  roundScores: RoundScore[]
  accuracy: number
  rank: number
  
  // Animations
  showAnimation: boolean
  animationType: 'tier_up' | 'tier_down' | 'on_fire' | 'blazing' | 'streak_broken' | 'upset' | 'streak' | null
  animationData: any
  
  // Actions
  initializeHeatMeter: (userId: string) => void
  updateAfterPick: (isCorrect: boolean, isUpset: boolean, round: BracketRoundType, basePoints: number, gameId?: string) => HeatMeterUpdate | null
  resetHeatMeter: () => void
  updateTotalScore: (points: number) => void
  updateRoundScore: (round: BracketRoundType, correct: boolean, points: number) => void
  triggerAnimation: (type: GamingState['animationType'], data?: any) => void
  clearAnimation: () => void
  updateRank: (newRank: number) => void
  resetScore: () => void
}

export const useGamingStore = create<GamingState>()(
  persist(
    (set, get) => ({
      // Initial state
      heatMeter: null,
      totalScore: 0,
      scoreBreakdown: {
        base: 0,
        heat: 0,
        upset: 0,
        powerUp: 0,
        total: 0
      },
      roundScores: [],
      accuracy: 0,
      rank: 0,
      showAnimation: false,
      animationType: null,
      animationData: null,

      // Initialize heat meter for a user
      initializeHeatMeter: (userId: string) => {
        const heatMeter = createHeatMeter(userId)
        set({ heatMeter })
      },

      // Update heat meter after a pick
      updateAfterPick: (isCorrect: boolean, isUpset: boolean, _round: BracketRoundType, basePoints: number, gameId?: string) => {
        const { heatMeter: currentHeatMeter, scoreBreakdown } = get()
        
        if (!currentHeatMeter) return null

        // Update heat meter
        const { meter: updatedMeter, update } = updateHeatMeter(currentHeatMeter, isCorrect, gameId)
        
        // Calculate points with heat multiplier
        const heatMultiplier = updatedMeter.multiplier
        const upsetMultiplier = isUpset ? 1.5 : 1.0 // Simple upset bonus for now
        const totalPoints = Math.floor(basePoints * heatMultiplier * upsetMultiplier)
        
        // Calculate breakdown
        const heatBonus = Math.floor(basePoints * (heatMultiplier - 1.0))
        const upsetBonus = isUpset ? Math.floor(basePoints * 0.5) : 0
        
        const newBreakdown: ScoreBreakdown = {
          base: scoreBreakdown.base + basePoints,
          heat: scoreBreakdown.heat + heatBonus,
          upset: scoreBreakdown.upset + upsetBonus,
          powerUp: scoreBreakdown.powerUp, // Will be updated separately
          total: scoreBreakdown.total + totalPoints
        }

        set({ 
          heatMeter: updatedMeter,
          scoreBreakdown: newBreakdown,
          totalScore: newBreakdown.total
        })

        // Trigger animations
        if (update.animation) {
          get().triggerAnimation(update.animation.type as GamingState['animationType'], {
            previousTier: update.previousTier,
            newTier: update.newTier,
            streak: update.newStreak
          })
        } else if (isUpset) {
          get().triggerAnimation('upset', { points: upsetBonus })
        }

        return update
      },

      // Reset heat meter
      resetHeatMeter: () => {
        const { heatMeter } = get()
        if (heatMeter) {
          const resetMeter = createHeatMeter(heatMeter.userId)
          set({ heatMeter: resetMeter })
        }
      },

      // Update total score
      updateTotalScore: (points: number) => {
        const { totalScore, scoreBreakdown } = get()
        const newTotal = totalScore + points
        set({ 
          totalScore: newTotal,
          scoreBreakdown: {
            ...scoreBreakdown,
            total: newTotal
          }
        })
      },

      // Update round score
      updateRoundScore: (round: BracketRoundType, correct: boolean, points: number) => {
        const { roundScores } = get()
        const existingRoundIndex = roundScores.findIndex(r => r.round === round)
        
        if (existingRoundIndex >= 0) {
          const existingRound = roundScores[existingRoundIndex]
          const updatedRound: RoundScore = {
            ...existingRound,
            picks: existingRound.picks + 1,
            correct: existingRound.correct + (correct ? 1 : 0),
            points: existingRound.points + points,
            accuracy: Math.round(((existingRound.correct + (correct ? 1 : 0)) / (existingRound.picks + 1)) * 100)
          }
          
          const newRoundScores = [...roundScores]
          newRoundScores[existingRoundIndex] = updatedRound
          set({ roundScores: newRoundScores })
        } else {
          const newRound: RoundScore = {
            round,
            picks: 1,
            correct: correct ? 1 : 0,
            points,
            accuracy: correct ? 100 : 0
          }
          set({ roundScores: [...roundScores, newRound] })
        }

        // Update overall accuracy
        const allRounds = get().roundScores
        const totalPicks = allRounds.reduce((sum, round) => sum + round.picks, 0)
        const totalCorrect = allRounds.reduce((sum, round) => sum + round.correct, 0)
        const overallAccuracy = totalPicks > 0 ? Math.round((totalCorrect / totalPicks) * 100) : 0
        
        set({ accuracy: overallAccuracy })
      },

      // Trigger animation
      triggerAnimation: (type: GamingState['animationType'], data?: any) => {
        set({ 
          showAnimation: true, 
          animationType: type, 
          animationData: data 
        })
      },

      // Clear animation
      clearAnimation: () => {
        set({ 
          showAnimation: false, 
          animationType: null, 
          animationData: null 
        })
      },

      // Update rank
      updateRank: (newRank: number) => {
        set({ rank: newRank })
      },

      // Reset score (for new bracket)
      resetScore: () => {
        set({
          totalScore: 0,
          scoreBreakdown: {
            base: 0,
            heat: 0,
            upset: 0,
            powerUp: 0,
            total: 0
          },
          roundScores: [],
          accuracy: 0,
          rank: 0
        })
      }
    }),
    {
      name: 'bracketblitz-gaming-store',
      partialize: (state) => ({
        heatMeter: state.heatMeter,
        totalScore: state.totalScore,
        scoreBreakdown: state.scoreBreakdown,
        roundScores: state.roundScores,
        accuracy: state.accuracy,
        rank: state.rank
      })
    }
  )
)